<template>
  <zm-dialog
    class="enjoy-tip-dialog"
    :visible.sync="visible"
    width="1300px"
    append-to-body
  >
    <!-- Set the default height for the container on mobile. After the video is loaded, the height of the container is determined by the video. -->
    <div
      class="video-container"
      ref="videoContainer"
      v-loading="!this.player"
      :style="($context.is.desktop || player) ? {} : { height: '200px' }"
      @click="pauseVideo"
    >
      <img
        v-show="!isVideoPlaying && this.player"
        class="video-control play-squircle-fill"
        src="../../assets/plans/play.svg"
        @click="changePlayState"
      />
      <zm-player
        :src="VIDEO_SOURCE"
        :poster="VIDEO_POSTER"
        @ready="handleVideoElReady"
      />
    </div>
    <div class="text-description">
      <img
        src="../../assets/plans/close.svg"
        class="close-btn"
        @click="handleCloseDialog"
      />
      <div class="tip-header">
        <img class="companion-logo" src="../../assets/plans/visitor_management.svg" alt="">
        {{ $t("billing.visitor_management.title") }}
      </div>
      <div class="tip-content">
        {{$t("billing.visitor_management.desc") }}
      </div>
      <price-list
        :showDecimalPoint="$context.is.desktop"
        :plan-category="planCategory"
        :unit-type="planUnitMap.user"
      />
      <div class="tip-list">
        <div v-for="item in tipList" :key="item" class="tip-list-item">
          <img src="../../assets/plans/check.svg" alt="">
          <div>{{ item }}</div>
        </div>
      </div>
    </div>
  </zm-dialog>
</template>

<script>
import get from 'lodash/get';
import { mapGetters } from 'vuex';
import { planTypes } from '@/common/constants.js';
import { plansof } from '@/utils/chain';
import PriceList from '@/components/price-list.vue';
import { planUnitMap } from '@/common/product-unit';
import { ConfirmComponent } from '../common/service-dialog/confirm';

export default {
  name: 'VisitorManagementDialog',
  components: { PriceList },
  mixins: [ConfirmComponent],
  data () {
    return {
      VIDEO_SOURCE: `${window.domainFromServer}fe-billing-plan-management/file/visitorManager.mp4`,
      VIDEO_POSTER: require('../../assets/plans/visitorManager.jpg'),
      visible: false,
      player: null,
      tipList: [
        this.$t('billing.visitor_management.plan_details.desc1'),
        this.$t('billing.visitor_management.plan_details.desc2'),
        this.$t('billing.visitor_management.plan_details.desc3'),
        this.$t('billing.visitor_management.plan_details.desc4')
      ],
      isVideoPlaying: false,
      videoStates: ['play', 'playing', 'pause', 'ended', 'error'],
      planUnitMap,
    };
  },
  mounted () {
  },
  beforeDestroy () {
    this.removeVideoListener();
  },
  computed: {
    ...mapGetters(['planMapFlat']),
    planCategory() {
      return get(plansof(this.planMapFlat).type(planTypes.VISITOR_MANAGEMENT).get(0), 'category');
    },
  },
  methods: {
    handleCloseDialog () {
      this.visible = false;
      this.player?.pause?.();
    },
    handleVideoElReady (player) {
      this.player = player;
      this.registerVideoEvent();
    },
    changePlayState () {
      const method = this.isVideoPlaying ? 'pause' : 'play';
      this.player[method]?.();
    },
    pauseVideo () {
      this.isVideoPlaying && this.player.pause();
    },
    registerVideoEvent () {
      const videoElement = this.$refs.videoContainer.querySelector('video');
      this.videoStates.forEach(state => videoElement?.addEventListener(state, this.onVideoStateChange));
    },
    removeVideoListener () {
      const videoElement = this.$refs.videoContainer.querySelector('video');
      this.videoStates.forEach(state => videoElement?.removeEventListener(state, this.onVideoStateChange));
      this.player = null;
    },
    onVideoStateChange ({ type }) {
      this.isVideoPlaying = type?.includes('play');
    }
  },
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

$font-size: 14px;

.enjoy-tip-dialog {
  .zm-dialog {
    padding: 0;
    border-radius: 8px;
    border: 0;
    overflow: hidden;
    @include media(mobile) {
      $verticalMargin: 7vh;
      margin-top: $verticalMargin !important;
      margin-bottom: 0 !important;
      height: calc(100% - #{$verticalMargin} * 2) !important;
    }
    @include media(tablet) {
      $verticalMargin: 15vh;
      margin-top: $verticalMargin !important;
      margin-bottom: 0 !important;
      height: calc(100% - #{$verticalMargin} * 2) !important;
    }
    @include media(mobile, tablet) {
      $dialogWidth: calc(100% - 24px);
      width: $dialogWidth !important;
      min-width: $dialogWidth !important;
      padding-top: 40px !important;
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    &__header {
      display: none;
      @include media(mobile, tablet) {
        flex-shrink: 0;
        width: 100%;
      }
    }

    &__body {
      display: flex;
      height: 600px;
      @include media(mobile, tablet) {
        flex-grow: 1;
        flex-direction: column;
        width: 100%;
        overflow: auto;
      }

      .video-container {
        width: 800px;
        height: auto;
        position: relative;
        @include media(mobile, tablet) {
          flex-shrink: 0;
          width: 100%;
          height: auto;
        }
        .video-player-layout {
          height: 100%;
        }
        .video-control {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 62px;
          height: 62px;
          transform: translate(-50%, -50%);
          cursor: pointer;
          z-index: 1;

          i {
            font-size: 40px;
            color: #00053d;
          }
        }

        video {
          width: 100%;
          height: 100%;
          // object-fit: cover;
          pointer-events: none;
        }

        .video-player-wrapper {
          @include media(desktop) {
            height: 100% !important;
          }
          
          .vjs-poster {
            background-color: #00053d;
          }

          .vjs-control-bar {
            display: none;
          }
        }
      }

      .video-js {
        border-radius: 12px 0 0 12px;
      }

      .text-description {
        width: 500px;
        padding: 32px 32px 16px;
        display: flex;
        flex-direction: column;
        @include media(mobile, tablet) {
          width: 100%;
          padding: 24px 0;
        }

        .close-btn {
          position: absolute;
          top: 24px;
          right: 24px;
          cursor: pointer;
          @include media(mobile, tablet) {
            top: 14px;
            right: 14px;
            width: 12px;
            height: auto;
          }
        }

        .tip-header {
          display: flex;
          align-items: center;
          color: #00053D;
          font-size: 22px;
          font-weight: 700;
          @include media(mobile, tablet) {
            font-size: 18px;
          }

          .companion-logo {
            width: 44px;
            height: 44px;
            margin-right: 16px;
            @include media(mobile, tablet) {
              margin-right: 8px;
              width: 32px;
              height: 32px;
            }
          }

          .add-on-new {
            margin-left: 8px;
            width: 45px;
            height: 20px;
            @include media(mobile, tablet) {
              margin-left: 4px;
              width: auto;
              height: 16px;
            }
          }

        }

        .tip-content {
          font-size: 14px;
          color: #666487;
          font-weight: 400;
          margin: 16px 0;
          line-height: 21px;
          font-family: Almaden Sans;
        }

        .tip-list {
          &__title {
            color: var(--day-foreground-foreground-strong, #00031F);
            /* label/M (M) */
            font-family: Almaden Sans;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: 120%; /* 19.2px */
            height: 27px;
          }
          &-item {
            display: flex;
            align-items: flex-start;
            line-height: 24px;
            color: #00031F;
            font-size: 16px;
            font-weight: 400;
            margin-top: 8px;

            > img {
              margin-right: 8px;
              margin-top: 4px;
            }
          }
        }
        .divider {
          width: 436px;
          height: 1px;
          background: #E0E0E6;
          margin: 16px 0;
          @include media(mobile, tablet) {
            width: 100%;
          }
        }
        .tips-bottom {
          color: var(--day-foreground-foreground-strong, #00031F);
          /* body/LF — S (R) */
          font-family: Almaden Sans;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 150%; /* 21px */
          padding-top: 4px;
        }
        .legal-terms {
          color: var(--neutral-neutral-08, #515079);
          /* body/LF — XS (R) */
          font-family: Almaden Sans;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 150%; /* 18px */
        }
        .bottom-text {
          margin-top: auto;
        }
      }
    }
  }
}
</style>
